import { defineComponent } from "vue";
import { mapState } from "pinia";
import { Container } from "aurelia-dependency-injection";

import config from "../../../../../../../common/config";
import { useHospiceConvertToPending } from "../../../../store/useHospiceConvertToPending";
import { ServiceLocations } from "../../../../../../../enums/ServiceLocationsEnum";
import { ApplicationEnum } from "../../../../../../../enums/ApplicationEnum";
import { formatDate } from "../../../../../../../common/vue-helpers/modifiers/value-modifier";
import { PatientService } from "../../../../../../../services/patient.service";
import { VendorsService } from "../../../../../../../services/vendor-service";
import { LookupService } from "../../../../../../../services/lookup-service";
import { ToastrService } from "../../../../../../../services/toastr.service";
import { EnumsService } from "../../../../../../../services/enums.service";
import { IGetVendorByIdQuery } from "../../../../../../../resources-vue/vue-interfaces/i-vendor";
import { Address } from "../../../../../../../models/address";
import { IUpdatedPlacesAddress } from "../../../../../../../resources-vue/vue-interfaces/i-places-autocomplete";
import {
    IGetVendorByName,
    IGetVendorByNameData,
    IQueryVendorByName
} from "../../../../../../../resources-vue/vue-interfaces/i-enum-by-name";
import { getHospiceRedirectionUrl } from "../../../../../../../common/get-hospice-redirection-url";

import { ServiceLocation } from "../../../../../../../resources-vue/vue-models/service-locations";
import FormSection from "../../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TimePicker from "../../../../../../../resources-vue/vue-custom-elements/TimePicker/TimePicker.vue";
import CountryTypeahead from "../../../../../../../resources-vue/vue-custom-elements/CountryTypeahead/CountryTypeahead.vue";
import TypeaheadInput from "../../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import StateTypeahead from "../../../../../../../resources-vue/vue-custom-elements/StateTypeahead/StateTypeahead.vue";
import PlacesAutocomplete from "../../../../../../../resources-vue/vue-custom-elements/PlacesAutocomplete/PlacesAutocomplete.vue";

export default defineComponent({
    components: {
        FormSection,
        DatePicker,
        TimePicker,
        CountryTypeahead,
        TypeaheadInput,
        StateTypeahead,
        PlacesAutocomplete
    },
    props: {
        service: {
            default: null
        },
        resetList: {
            type: Function
        },

        closeServiceForm: {
            type: Function
        },

        showServiceForm: {
            type: Boolean
        },
        isEdit: {
            type: Boolean
        },
        facilityId: {
            type: String
        },
        showCode: {
            type: Boolean,
            default: true
        },

        openEditServiceLocation: {
            type: Function
        }
    },

    data(vm) {
        return {
            _toastrService: null,
            _patientService: null,
            selectedCountry: null,
            _vendorsService: null,
            _enumService: null,
            selectedFacility: null,
            _autoCompleteAddress: null,
            selectedState: null,
            unitedStatesCountryCode: "",
            _lookupService: null as LookupService,
            serviceLocationsOptions: [] as IGetVendorByNameData[],
            flaggedServiceLocations: [] as IGetVendorByNameData[],
            isLoading: false,
            formatDate
        };
    },

    emits: ["update:facilityId", "update:service"],

    created() {
        this._patientService = Container.instance.get(PatientService);
        this._toastrService = Container.instance.get(ToastrService);
        this._lookupService = Container.instance.get(LookupService);
        this._vendorsService = Container.instance.get(VendorsService);
        this._enumService = Container.instance.get(EnumsService);
    },

    async mounted() {
        if (!this.facilityId) {
            this.selectedFacility = null;
        } else {
            const payload: IGetVendorByIdQuery = {
                application: Number(this.application),
                accountId: this.accountId as string,
                id: this.facilityId
            };
            const selectedVendor = await this._vendorsService.getVendorById(payload);
            if (!!selectedVendor) {
                this.selectedFacility = {
                    name: selectedVendor.name,
                    value: selectedVendor.id
                };
                this.service.addressLine1 = selectedVendor.address.addressLine1;
                this.service.addressLine2 = selectedVendor.address.addressLine2;
                this.service.city = selectedVendor.address.city;
                this.service.state = selectedVendor.address.state;
                this.service.zipCode = selectedVendor.address.zipCode;
                this.service.county = selectedVendor.address.county;
                this.service.country = selectedVendor.address.country;
                this.service.isValidated = selectedVendor.address.isValidated;
                this.service.latitude = selectedVendor.address.latitude;
                this.service.longitude = selectedVendor.address.longitude;
                this.service.servicesLocation = selectedVendor.servicesLocation;
                this.service.isServiceLocation = selectedVendor.isServiceLocation;
            }
        }
        this.unitedStatesCountryCode = await this._lookupService.getUnitedStatesCode();
        if (!this.service.country) {
            this.service.country = this.unitedStatesCountryCode;
        }
        await this.getServiceLocation();
    },
    watch: {
        selectedFacility: {
            async handler(newValue, oldValue) {
                if (newValue !== oldValue) {
                    if (!!this.selectedFacility && this.selectedFacility.value !== this.facilityId) {
                        this.$emit("update:facilityId", this.selectedFacility.value);
                        const payload: IGetVendorByIdQuery = {
                            application: Number(this.application),
                            accountId: this.accountId as string,
                            id: this.selectedFacility.value
                        };
                        const selectedVendor = await this._vendorsService.getVendorById(payload);
                        if (!!selectedVendor) {
                            this.service.addressLine1 = selectedVendor.address.addressLine1;
                            this.service.addressLine2 = selectedVendor.address.addressLine2;
                            this.service.city = selectedVendor.address.city;
                            this.service.state = selectedVendor.address.state;
                            this.service.zipCode = selectedVendor.address.zipCode;
                            this.service.county = selectedVendor.address.county;
                            this.service.country = selectedVendor.address.country;
                            this.service.isValidated = selectedVendor.address.isValidated;
                            this.service.latitude = selectedVendor.address.latitude;
                            this.service.longitude = selectedVendor.address.longitude;
                            this.service.servicesLocation = selectedVendor.servicesLocation;
                            this.service.isServiceLocation = selectedVendor.isServiceLocation;
                        }
                    } else if (!this.selectedFacility) {
                        this.$emit("update:facilityId", null);
                        this.$emit("update:service", new Address());
                        this.service.Code = "";
                        if (this.isUnitedStates) {
                            this.service.initValidations();
                        } else {
                            this.service.initInternationalValidation();
                        }
                    }
                }
            },
            deep: true
        },

        selectedState() {
            const temp = { ...this.service };
            temp.state = this.selectedState.value;
            this.service.state = this.selectedState.value;
            this.$emit("update:service", temp);
        }
    },
    computed: {
        ...mapState(useHospiceConvertToPending, ["application", "patientDetails", "accountId", "patientId"]),
        isUnitedStates() {
            return this.selectedCountry?.name.toLowerCase() === "united states of america";
        },
        filteredServiceLocations() {
            if (!this.serviceLocationsOptions || !this.flaggedServiceLocations) this.getServiceLocation();
            if (!this.selectedFacility || this.selectedFacility === "") {
                if (!this.serviceLocationsOptions) {
                    return [{ Name: "loading...", Value: null }] as IGetVendorByNameData[];
                }
                return this.serviceLocationsOptions.filter((location) => location.Value.toString() === "1");
            }
            if (this.service.isServiceLocation) {
                let flaggedLocationName = [] as string[];
                this.flaggedServiceLocations
                    .filter((location) => this.service.servicesLocation.includes(location.Value))
                    .forEach((val) => {
                        flaggedLocationName.push(val.Name);
                    });
                return this.serviceLocationsOptions.filter((location) => flaggedLocationName.includes(location.Name));
            }
            return this.serviceLocationsOptions;
        }
    },

    methods: {
        handleDateChange(e: any) {
            this.service.Start = e.target.value;
        },
        updateDate(date: string) {
            this.service.Start = date;
        },
        changeStartTime(time: string) {
            this.service.StartTime = time;
        },
        handleSave() {
            if (this.isEdit) {
                this.updateServiceLocation();
            } else {
                this.addServiceLocation();
            }
        },
        async getVendorList(filter: string, limit: number) {
            const payload = {
                accountId: this.accountId,
                name: filter,
                providerIds: [this.patientDetails.providerId],
                application: Number(this.application)
            };
            const facilityList = await this._vendorsService.getVendorByTerm(payload);
            return facilityList.map((facility: any) => ({
                name: facility.name,
                value: facility.id
            }));
        },
        updateFacility(value: any) {
            this.selectedFacility = value;
        },
        updateSelectedCountry(value: any) {
            this.selectedCountry = value;
            let temp = { ...this.service };
            if (!!this.selectedCountry) {
                temp.country = this.selectedCountry.value;
            } else {
                temp.country = "";
            }
            this.$emit("update:service", temp);
        },
        handleDirectionToFacility() {
            window.open(
                `${getHospiceRedirectionUrl(this.accountId as string)}/create/vendor?accountId=${this.accountId}`,
                "_blank"
            );
        },
        handleZipcodeChange(e: any) {
            this.service.zipCode = e.target.value;
        },
        async addServiceLocation() {
            const payload = {
                ...this.service,
                agencyId: this.accountId,
                application: Number(this.application),
                patientServiceAddress: {
                    AddressLine1: this.service.addressLine1,
                    AddressLine2: this.service.addressLine2,
                    City: this.service.city,
                    State: this.service.state,
                    ZipCode: this.service.zipCode,
                    County: this.service.county,
                    Country: this.service.country
                },
                FacilityId: this.selectedFacility?.value
            } as ServiceLocation;
            try {
                await this._patientService.addServiceLocation(payload, this.patientId);
                this.closeServiceForm();
                this._toastrService.success({
                    title: `Service Location added successfully`
                });
                this.resetList();
            } catch (err) {
                console.log(err);
                this._toastrService.error({
                    title: `Failed to add Service Location `,
                    message: `Try Again`
                });
            }
        },
        async updateServiceLocation() {
            const payload = {
                ...this.service,
                agencyId: this.accountId,
                application: Number(this.application),
                FacilityId: this.selectedFacility?.value
            } as ServiceLocation;
            try {
                this.isLoading = true;
                await this._patientService.updateServiceLocation(payload, this.patientId, this.service.Id);
                this.resetList();
                this.closeServiceForm();
                this._toastrService.success({
                    title: `Service Location updated successfully`
                });
            } catch (err) {
                console.log(err);
                this.closeServiceForm();
                this._toastrService.error({
                    title: `Failed to update Service Location `,
                    message: `Try Again`
                });
            } finally {
                this.isLoading = false;
            }
        },
        placesChangedCallBack(params: IUpdatedPlacesAddress) {
            if (!params) {
                return;
            }
            Object.assign(this._autoCompleteAddress, params);
            this.service.addressLine1 = params.addressLine1;
            this.service.city = params.city;
            this.service.county = params.county;
            this.service.state = params.state;
            this.service.zipCode = params.zipCode;
            this.service.latitude = params.latitude;
            this.service.longitude = params.longitude;
            this.service.isValidated = true;
            this.service.isBypassed = false;
        },
        updateSelectedState(value: any) {
            this.selectedState = value;
            this.service.state = value;
        },
        async getServiceLocation() {
            const serviceLocationPayload: IQueryVendorByName = {
                Application: Number(this.application) as ApplicationEnum,
                AgencyId: this.accountId as string,
                Values: ["FlaggedServiceLocation", "ServiceLocation"]
            };
            const serviceLocationResponse = await this._enumService.getEnumByName(serviceLocationPayload);
            if (serviceLocationResponse && serviceLocationResponse.data) {
                let response = serviceLocationResponse as IGetVendorByName;
                this.flaggedServiceLocations = response.data.FlaggedServiceLocation;
                this.serviceLocationsOptions = response.data.ServiceLocation;
            }
        }
    }
});
